import React, { type FunctionComponent } from 'react'
import styled from '@emotion/styled'
import range from 'lodash/range'
import { FormattedMessage } from 'react-intl'
import { colors } from 'ic-snacks'
import { LoadingDots } from '@retailer-platform/shared-components'
import moment from 'moment'
import {
  addInterval,
  intervalDifference,
  INTERVALS_PER_HOUR,
  percentageOfElapsedTime,
} from 'domains/shifts/components/utils/intervalUtils'
import { isInPast, isToday } from 'legacy/common/typedDateUtils'
import { ShiftType, type TransformedShift } from 'domains/shifts/components/types'
import { useShiftManagementContext } from 'domains/shifts/components/contexts/ShiftManagementContext'
import ShiftCell from 'domains/shifts/components/ShiftCell'
import ShiftsRowErrorBoundary from 'domains/shifts/components/ShiftsRowErrorBoundary'
import ShiftRowCurrentBarMarker from 'domains/shifts/components/ShiftRowCurrentHourBarMarker'
import {
  type NotificationsContextValue,
  withNotificationsContext,
} from 'legacy/contexts/notifications/NotificationsContext'
import { cellIsCurrentInterval, isStoreClosed } from 'domains/shifts/components/utils/shiftsUtils'
import StyledCell from 'domains/shifts/components/StyledCell'
import { useShiftsStateContext } from 'domains/shifts/components/contexts/ShiftsStateContext'
import { calculateShiftModalStyles } from './utils/calculateShiftModalStyles'

const disabledMessageIds = {
  [ShiftType.Picking]: 'shiftsDomain.picking',
  [ShiftType.Running]: 'shiftsDomain.running',
  [ShiftType.FullService]: 'shiftsDomain.driving',
}

const Grid = styled.div<{ columns: number }>(({ columns }) => ({
  flexGrow: 1,
  display: 'grid',
  gridTemplateColumns: `repeat(${columns}, 1fr)`,
  gridTemplateRows: '64px',
  background: colors.GRAY_93,
  gridGap: '1px',
}))
const BackgroundCell = styled.div<{
  even: boolean
  number: number
  isClosed: boolean
  isDisabled: boolean
}>(({ isClosed, even, number, isDisabled }) => ({
  width: '100%',
  height: '100%',
  backgroundColor: even ? colors.WHITE : colors.GRAY_97,
  gridRow: '1/1',
  gridColumn: `${number + 1}/${number + 2}`,
  position: 'relative',
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  '&:after': isClosed && {
    zIndex: 1,
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: colors.GRAY_74,
    opacity: 0.4,
  },
}))

interface Props extends NotificationsContextValue {
  warehouseLocationId: string
  first: boolean
  even: boolean
  shifts: TransformedShift[]
  activeShiftType: ShiftType
  onAddShift(start: Date, end: Date, onCall: boolean, modalStyles?: Object): void
  zoneOffset: number
  shopperId: number
  devicePrioritizationEnabled: boolean
  recurShiftsToBeNonRecur: number[]
  rowRefs: React.RefObject<HTMLDivElement>[]
  ultrafastCrossDayDeliveryEnabled?: boolean
}

const StyledLoadingCell = styled(StyledCell)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

const ShiftRow: FunctionComponent<Props> = props => {
  // Prevent duplicate keys
  let newKeyCounter = 0
  const {
    storeHours: { actualStoreOpen, actualStoreClose, regularStoreOpen, regularStoreClose },
  } = useShiftManagementContext()

  const onCellClicked = (index: number) => {
    const { onAddShift, rowRefs } = props
    const startTime = addInterval(regularStoreOpen, index)
    const endTime = addInterval(startTime, INTERVALS_PER_HOUR)
    const modalStyles = calculateShiftModalStyles(rowRefs[index])

    if (isInPast(startTime)) {
      props.notifyError(<FormattedMessage id="shiftsDomain.createFailed.past" />)
      return
    }

    if (startTime >= actualStoreClose || startTime < actualStoreOpen) {
      props.notifyError(<FormattedMessage id="shiftsDomain.createFailed.invalidHours" />)
      return
    }

    return onAddShift(startTime, endTime, false, modalStyles)
  }

  const getPercentageOfElapsedTimeOfCell = (index: number) => {
    const startTimeOfCell = addInterval(actualStoreOpen, index)
    const endTimeOfCell = addInterval(startTimeOfCell, 1)
    return percentageOfElapsedTime(startTimeOfCell, endTimeOfCell, new Date())
  }

  const { shifts, activeShiftType, first, even, rowRefs, shopperId } = props
  const {
    shiftsState: { selectedRecommendationShiftRange },
  } = useShiftsStateContext()

  const {
    shiftsState: { recommendationType, shopperIdBeingEdited: selectedRecommendationShopperId },
  } = useShiftsStateContext()

  const numberColumns = intervalDifference(regularStoreOpen, regularStoreClose)

  return (
    <Grid columns={numberColumns}>
      {range(numberColumns).map(i => (
        <BackgroundCell
          ref={rowRefs[i]}
          data-testid={`shifts-row-background-cell-${i}`}
          even={even}
          key={i}
          number={i}
          isClosed={isStoreClosed(i, { actualStoreClose, actualStoreOpen, regularStoreOpen })}
          isDisabled={isInPast(addInterval(regularStoreOpen, i))}
          onClick={() => {
            onCellClicked(i)
          }}
        >
          {isToday(regularStoreOpen) && cellIsCurrentInterval(i, regularStoreOpen) && (
            <ShiftRowCurrentBarMarker
              renderMarker={first}
              percentageFromLeft={getPercentageOfElapsedTimeOfCell(i)}
            />
          )}
        </BackgroundCell>
      ))}
      <ShiftsRowErrorBoundary>
        {shifts.map((shift, index) => {
          // safeEndTime here is to make sure the shift won't break the UI
          // We want to support WHL that's open 24 hours and they sometimes schedule shift cross the midnight
          // For example, 10pm to 2am
          // In this case, we will display 10pm to 12am on today's Shifts UI
          // but we will introduce another end time for just the label.
          // Essentially, the shift cell label will show 10pm to 2am but the shift will span from 10pm to 12am
          const safeEndTime = moment(shift.ends_at).isAfter(moment(regularStoreClose))
            ? moment(regularStoreClose).toDate()
            : moment(shift.ends_at).toDate()
          return (
            <ShiftCell
              id={shift.id}
              draggable={Boolean(shift.id)}
              busy={shift.busy}
              key={shift.id || `new${(newKeyCounter += 1)}`}
              start={shift.starts_at}
              end={safeEndTime}
              endLabelTime={shift.ends_at}
              rangeStart={regularStoreOpen}
              rangeEnd={regularStoreClose}
              isRecurring={!!shift.recurring_shift_id}
              recurringShiftId={shift.recurring_shift_id}
              minHour={index === 0 ? regularStoreOpen : shifts[index - 1].ends_at}
              maxHour={
                index === shifts.length - 1 ? regularStoreClose : shifts[index + 1].starts_at
              }
              disabled={shift.shift_type !== activeShiftType}
              disabledComponent={<FormattedMessage id={disabledMessageIds[shift.shift_type]} />}
              daysOfWeek={shift.days_of_week}
              recurStartDate={shift.recur_start_date}
              recurEndDate={shift.recur_end_date}
              onCall={shift.on_call}
              isPlaceholder={shift.placeholder}
              {...props}
            />
          )
        })}
      </ShiftsRowErrorBoundary>
      {selectedRecommendationShopperId === shopperId &&
        selectedRecommendationShiftRange &&
        recommendationType && (
          <StyledLoadingCell
            isPlaceholder={true}
            disabled={false}
            firstColumn={selectedRecommendationShiftRange.start}
            lastColumn={selectedRecommendationShiftRange.end}
            enhancedRecurringShiftsEnabled={false}
          >
            <LoadingDots height={10} fill={colors.WHITE} />
          </StyledLoadingCell>
        )}
    </Grid>
  )
}

export default withNotificationsContext(ShiftRow)
